import { AuthenticationError, PermissionDeniedError, Whop } from "@whop/sdk";
import type { Identity } from "./types";

/**
 * Whop API boundary for authentication.
 *
 * Validates a key by calling the requesting-account endpoint through the SDK.
 * The SDK adds the `Bearer ` prefix itself, so we pass the raw token. The SDK
 * throws typed errors we map to a discriminated result:
 *   - `AuthenticationError` (401) → the key is invalid → reject login.
 *   - `PermissionDeniedError` (403) → the key is valid but missing the
 *     required scope → accept login with best-effort identity.
 */

const DEFAULT_BASE_URL = "https://api.whop.com/api/v1";

export function apiBaseUrl(env: NodeJS.ProcessEnv = process.env): string {
	return env.WHOP_API_BASE_URL?.trim() || DEFAULT_BASE_URL;
}

/**
 * Base URL for the OAuth endpoints (`/oauth/authorize`, `/oauth/token`,
 * `/oauth/userinfo`), which live at the API host root rather than under
 * `/api/v1`. Tolerates a base URL with or without the `/api/v1` suffix and a
 * trailing slash.
 */
export function oauthBaseUrl(env: NodeJS.ProcessEnv = process.env): string {
	return apiBaseUrl(env)
		.replace(/\/+$/, "")
		.replace(/\/api\/v1$/, "");
}

export type ValidateResult =
	| { status: "valid"; identity: Identity }
	| { status: "underscoped"; identity: Identity }
	| { status: "invalid" }
	| { status: "error"; message: string };

export async function validateApiKey(
	apiKey: string,
	baseUrl: string = apiBaseUrl(),
): Promise<ValidateResult> {
	const whop = new Whop({ apiKey, baseURL: baseUrl });
	try {
		const account = await whop.accounts.me();
		return {
			status: "valid",
			identity: { id: account.id, title: account.title, route: account.route },
		};
	} catch (error) {
		if (error instanceof AuthenticationError) return { status: "invalid" };
		if (error instanceof PermissionDeniedError)
			return { status: "underscoped", identity: {} };
		return {
			status: "error",
			message: error instanceof Error ? error.message : String(error),
		};
	}
}
